import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PetVisual, usePetHas3d, useIn3dOnly, useViewMode, ViewModeToggle, PETS } from '../visuals';
import './MeetThePets.css';

/** Every species, from the site's one pet registry. */
export const ROSTER = PETS.map((p) => [p.id, p.name]);

/** Pets that take a turn in the spotlight. */
const SPOTLIGHT = ['cat', 'baby_dragon', 'axolotl', 'mocchi_rabbit', 'fox', 'star_sprite', 'panda', 'pocket_phoenix'];

const MOODS = [
  { id: 'neutral', label: 'Idle' },
  { id: 'happy', label: 'Happy' },
  { id: 'love', label: 'Love' },
  { id: 'energetic', label: 'Hyped' },
  { id: 'sleepy', label: 'Sleepy' },
  { id: 'sad', label: 'Sad' },
  { id: 'hungry', label: 'Hungry' },
];

const nameOf = Object.fromEntries(ROSTER);

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * All forty-two pets in their voxel-chibi art: one in the spotlight, idling,
 * with every face it can pull a tap away, and the whole roster parading past
 * below it.
 */
export default function MeetThePets() {
  // In 3D, only the pets with models take the stage and the parade.
  const in3d = useIn3dOnly();
  const spotlight = in3d.on ? ROSTER.map(([id]) => id).filter(in3d.pet) : SPOTLIGHT;
  const roster = ROSTER.filter(([id]) => in3d.pet(id));
  const [spot, setSpot] = useState(0);
  const [mood, setMood] = useState('neutral');
  const [held, setHeld] = useState(false);

  // A new pet steps up every few seconds, unless someone is trying faces.
  useEffect(() => {
    if (held || prefersReducedMotion()) return undefined;
    const timer = setInterval(() => {
      setSpot((i) => i + 1);
      setMood('neutral');
    }, 7000);
    return () => clearInterval(timer);
  }, [held]);

  const petId = spotlight[spot % spotlight.length];
  const { mode } = useViewMode();
  // The moods are faces drawn on the art; a model has none to pull.
  const has3d = usePetHas3d(petId) === true;
  const showsModel = mode === '3d' && has3d;

  return (
    <section className="meet-section">
      <div className="container meet-layout">
        <div className="meet-spotlight" onMouseEnter={() => setHeld(true)} onMouseLeave={() => setHeld(false)}>
          <div className="meet-stage">
            <div className="meet-glow" />
            <div className="meet-pet" key={`${petId}-${mood}`}>
              <PetVisual petId={petId} size={240} mood={mood} live showMissing />
            </div>
          </div>
          <div className="meet-name">{nameOf[petId]}</div>
          {!showsModel && <div className="meet-moods" role="group" aria-label="Pick a mood">
            {MOODS.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`meet-mood ${mood === m.id ? 'active' : ''}`}
                onClick={() => { setMood(m.id); setHeld(true); }}
              >
                {m.label}
              </button>
            ))}
          </div>}
        </div>

        <div className="meet-info">
          <span className="meet-eyebrow">New look</span>
          <ViewModeToggle className="meet-view-toggle" compact />
          <h2>Meet the Petlings</h2>
          <p>
            Every one of the forty-two pets has been redrawn as a glossy little voxel figure, with a face for every
            mood: happy, hyped, sleepy, a bit sad, hungry, and heart-eyed when you give them a pat. In the app they
            breathe, blink and hop for joy.
          </p>
          <div className="meet-picker">
            {spotlight.map((id, i) => {
              return (
                <button key={id} type="button" className={`meet-pick ${i === spot % spotlight.length ? 'active' : ''}`}
                        onClick={() => { setSpot(i); setMood('neutral'); setHeld(true); }} title={nameOf[id]}>
                  <PetVisual petId={id} size={48} />
                </button>
              );
            })}
          </div>
          <Link to="/catalog" className="btn btn-secondary">Browse all 42</Link>
        </div>
      </div>

      <div className="meet-parade" aria-label="All forty-two pets">
        <div className="meet-parade-track">
          {[...roster, ...roster].map(([id, name], i) => {
            return (
              <Link key={`${id}-${i}`} to="/catalog" className="meet-parade-pet" title={name}
                    aria-hidden={i >= roster.length} tabIndex={i >= roster.length ? -1 : 0}>
                <PetVisual petId={id} size={72} />
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
